/**
 * Utilities para manejo de autenticación con Clerk
 * Proporciona funciones para obtener tokens, información del usuario y manejar autenticación
 */

import { useUser, useAuth } from '@clerk/clerk-react';

/**
 * Obtiene el token de Clerk del usuario actual
 * Funciona tanto en contexto de React como fuera de él
 */
export const getClerkToken = async () => {
  try {
    // Método 1: Si estamos en contexto de React con acceso al hook
    if (typeof window !== 'undefined' && window.__CLERK_USER__) {
      const user = window.__CLERK_USER__;
      if (user && typeof user.getToken === 'function') {
        return await user.getToken();
      }
    }

    // Método 2: Intentar obtener desde la instancia global de Clerk
    if (typeof window !== 'undefined' && window.Clerk) {
      const session = window.Clerk.session;
      if (session && typeof session.getToken === 'function') {
        return await session.getToken();
      }
    }

    // Método 3: Buscar en el almacenamiento local (último recurso)
    if (typeof window !== 'undefined') {
      // Clerk a veces almacena tokens en el localStorage con claves específicas
      const clerkPrefix = '__clerk_';
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(clerkPrefix)) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (data.token && typeof data.token === 'string') {
              return data.token;
            }
          } catch (e) {
            // Continuar con la siguiente clave
          }
        }
      }
    }

    console.warn('[ClerkUtils] No se pudo obtener token de Clerk');
    return null;
  } catch (error) {
    console.error('[ClerkUtils] Error al obtener token de Clerk:', error);
    return null;
  }
};

/**
 * Verifica si el usuario está autenticado
 */
export const isAuthenticated = () => {
  try {
    if (typeof window !== 'undefined' && window.Clerk) {
      return !!window.Clerk.session;
    }
    
    // Fallback: verificar si hay un token disponible
    return getClerkToken() !== null;
  } catch (error) {
    console.error('[ClerkUtils] Error al verificar autenticación:', error);
    return false;
  }
};

/**
 * Obtiene información básica del usuario actual
 */
export const getCurrentUser = () => {
  try {
    if (typeof window !== 'undefined' && window.__CLERK_USER__) {
      const user = window.__CLERK_USER__;
      return {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.fullName,
        imageUrl: user.imageUrl,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt
      };
    }

    if (typeof window !== 'undefined' && window.Clerk?.user) {
      const user = window.Clerk.user;
      return {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.fullName,
        imageUrl: user.imageUrl,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt
      };
    }

    return null;
  } catch (error) {
    console.error('[ClerkUtils] Error al obtener usuario actual:', error);
    return null;
  }
};

/**
 * Hook personalizado para usar autenticación de Clerk
 */
export const useClerkAuth = () => {
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();
  const { getToken, signOut, isLoaded: authLoaded } = useAuth();

  // Actualizar referencia global del usuario para uso fuera de React
  if (typeof window !== 'undefined' && user) {
    window.__CLERK_USER__ = user;
  }

  const getUserToken = async () => {
    try {
      if (getToken) {
        return await getToken();
      }
      return await getClerkToken();
    } catch (error) {
      console.error('[useClerkAuth] Error al obtener token:', error);
      return null;
    }
  };

  const handleSignOut = async () => {
    try {
      if (signOut) {
        await signOut();
      }
      
      // Limpiar referencias globales
      if (typeof window !== 'undefined') {
        window.__CLERK_USER__ = null;
      }
    } catch (error) {
      console.error('[useClerkAuth] Error al cerrar sesión:', error);
    }
  };

  return {
    user,
    isLoaded: userLoaded && authLoaded,
    isSignedIn,
    getToken: getUserToken,
    signOut: handleSignOut,
    isAuthenticated: isSignedIn && !!user
  };
};

/**
 * Configura interceptores automáticos para añadir token a requests
 */
export const setupClerkInterceptors = (apiClient) => {
  // Interceptor para añadir token automáticamente
  apiClient.addRequestInterceptor(async (config) => {
    const token = await getClerkToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Interceptor para manejar errores 401 (token expirado/inválido)
  apiClient.addErrorInterceptor(async (error) => {
    if (error.response?.status === 401) {
      console.warn('[ClerkUtils] Token inválido o expirado, limpiando sesión');
      
      // Intentar cerrar sesión en Clerk
      if (typeof window !== 'undefined' && window.Clerk?.signOut) {
        try {
          await window.Clerk.signOut();
        } catch (signOutError) {
          console.error('[ClerkUtils] Error al cerrar sesión:', signOutError);
        }
      }

      // Limpiar referencias globales
      if (typeof window !== 'undefined') {
        window.__CLERK_USER__ = null;
      }

      // Opcional: redirigir a login
      if (typeof window !== 'undefined' && window.location) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/sign-in') {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  });
};

/**
 * Verifica si el token actual es válido
 */
export const validateToken = async () => {
  try {
    const token = await getClerkToken();
    if (!token) return false;

    // Intentar decodificar el JWT para verificar expiración
    const payload = parseJWT(token);
    if (!payload) return false;

    // Verificar expiración
    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < currentTime) {
      console.warn('[ClerkUtils] Token expirado');
      return false;
    }

    return true;
  } catch (error) {
    console.error('[ClerkUtils] Error al validar token:', error);
    return false;
  }
};

/**
 * Parsea un JWT sin verificar la firma (solo para leer payload)
 */
export const parseJWT = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('[ClerkUtils] Error al parsear JWT:', error);
    return null;
  }
};

/**
 * Obtiene información del token actual
 */
export const getTokenInfo = async () => {
  try {
    const token = await getClerkToken();
    if (!token) return null;

    const payload = parseJWT(token);
    if (!payload) return null;

    return {
      userId: payload.sub,
      email: payload.email,
      issuedAt: payload.iat ? new Date(payload.iat * 1000) : null,
      expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
      issuer: payload.iss,
      audience: payload.aud,
      raw: payload
    };
  } catch (error) {
    console.error('[ClerkUtils] Error al obtener información del token:', error);
    return null;
  }
};

/**
 * Fuerza la renovación del token
 */
export const refreshToken = async () => {
  try {
    if (typeof window !== 'undefined' && window.Clerk?.session) {
      const session = window.Clerk.session;
      if (typeof session.getToken === 'function') {
        // Forzar renovación pasando opciones específicas
        return await session.getToken({ skipCache: true });
      }
    }

    console.warn('[ClerkUtils] No se pudo renovar el token');
    return null;
  } catch (error) {
    console.error('[ClerkUtils] Error al renovar token:', error);
    return null;
  }
};

/**
 * Configura listener para cambios de autenticación
 */
export const setupAuthListener = (callback) => {
  if (typeof window !== 'undefined' && window.Clerk) {
    return window.Clerk.addListener('session', callback);
  }
  
  console.warn('[ClerkUtils] No se pudo configurar listener de autenticación');
  return null;
};